import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AudioService, AudioVisibility } from 'src/services/audio.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';

@Component({
  selector: 'vex-floating-audio-player',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  template: `
    @if (visible) {
      <div class="fixed bottom-6 right-6 z-[9999] bg-black shadow-lg rounded px-4 py-1 flex items-center gap-2">
        <strong class="text-white">{{ audioName || 'Audio' }}</strong>
        <button mat-icon-button class="!text-white" (click)="togglePlayPause()">
          <mat-icon [svgIcon]="isPlaying ? 'mat:pause' : 'mat:play_arrow'"></mat-icon>
        </button>
      </div>
    }
  `
})
export class FloatingAudioPlayerComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private audioService = inject(AudioService);
  visible = false;
  audioName = '';
  isPlaying = false;
  private audioVisibility: AudioVisibility = 'visible';
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    combineLatest([
      this.audioService.isPlaying$,
      this.audioService.visibility$
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(([isPlaying, visibility]) => {
      this.isPlaying = isPlaying;
      this.audioVisibility = visibility;

      if (isPlaying && visibility === 'visible') {
        this.visible = true;
        this.clearHideTimeout();
      } else if (!isPlaying && this.audioVisibility === 'visible') {
        this.startHideTimeout();
      } else if (visibility === 'background') {
        this.visible = false;
        this.clearHideTimeout();
      }
    });

    this.audioService.currentAudioName$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(name => {
      this.audioName = name;
    });
  }

  ngOnDestroy(): void {
    this.clearHideTimeout();
  }

  togglePlayPause(): void {
    this.audioService.togglePlayPause();
  }

  private startHideTimeout(): void {
    this.clearHideTimeout();
    this.hideTimeout = setTimeout(() => {
      this.visible = false;
    }, 20000);
  }

  private clearHideTimeout(): void {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }
}
